import { type ClassValue, clsx } from 'clsx'
import { DateRange } from 'react-day-picker'
import { twMerge } from 'tailwind-merge'

import { ComparisonSelection } from '@/components/ui/charts/types'
import { ChartType } from '@/components/ui/charts/types/chart-props'
import { TenantKey } from '@/config/types'
import {
  ALL_STORES_BLANKSTREET_JOINED,
  SAME_STORES_DEV_JOINED,
} from '@/constants/BRANCHES'
import {
  LABOR_METRIC_FILTERS,
  PNL_METRIC_FILTERS,
  SALES_METRIC_FILTERS,
  SPEED_OF_SERVICE_METRIC_FILTERS,
  SPEND_METRIC_FILTERS,
} from '@/constants/CHART_CONSTANTS'
import { CountryFilterType } from '@/store/filterSlice'
import { KpiUserAssignmentsModel, Region } from '@/types'
import { Interval } from '@/types/clarity/metrics/sales'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatNumber(
  value: number | string | null | undefined,
  trendType: 'money' | 'percentage' | 'number' | 'hour' | 'seconds',
  showUnit: boolean = true,
  country?: CountryFilterType
): string {
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '-'
  }

  const numberValue = Number(value)

  const fractionDigits = getFractionDigits(numberValue, trendType)

  let formattedValue = numberValue.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })

  if (formattedValue.endsWith('.0')) {
    formattedValue = formattedValue.slice(0, -2)
  } else if (formattedValue.endsWith('.00')) {
    formattedValue = formattedValue.slice(0, -3)
  }

  if (trendType === 'money') {
    if (country === 'UK') {
      return `£${formattedValue}`
    }
    return `$${formattedValue}`
  } else if (trendType === 'percentage') {
    return `${formattedValue}%`
  } else if (trendType === 'hour') {
    return `${formattedValue}${showUnit ? 'hr' : ''}`
  } else if (trendType === 'seconds') {
    return `${formattedValue}${showUnit ? 'secs' : ''}`
  } else {
    return formattedValue
  }
}

export function dateToString(
  date: DateRange | undefined,
  type: 'from' | 'to',
  format: 'yyyy-mm-dd' | 'yyyy-mm' | 'yyyy' = 'yyyy-mm-dd'
): string {
  if (type === 'to' && date?.to === undefined) {
    return date?.from ? formatDateToString(date.from, format) : ''
  } else {
    return date?.[type] ? formatDateToString(date[type], format) : ''
  }

  function formatDateToString(
    date: Date,
    format: 'yyyy-mm-dd' | 'yyyy-mm' | 'yyyy'
  ): string {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    if (format === 'yyyy-mm') {
      return `${year}-${month}`
    } else if (format === 'yyyy') {
      return `${year}`
    } else {
      return `${year}-${month}-${day}`
    }
  }
}

function getFractionDigits(
  value: number,
  trendType: 'money' | 'percentage' | 'number' | 'hour' | 'seconds'
): number {
  let fractionDigits = 0

  switch (trendType) {
    case 'money':
      fractionDigits =
        Math.abs(value) >= 1000 ? 0 : Math.abs(value) >= 100 ? 1 : 2
      break
    case 'percentage':
      fractionDigits = Math.abs(value) >= 10 ? 1 : 2
      break
    case 'number':
      fractionDigits =
        Math.abs(value) >= 1000 ? 0 : Math.abs(value) >= 100 ? 1 : 2
      break
    case 'hour':
    case 'seconds':
      fractionDigits =
        Math.abs(value) >= 100 ? 0 : Math.abs(value) >= 10 ? 1 : 2
      break
  }

  return fractionDigits
}

export function getTrending(
  content: string | number,
  trendType: 'money' | 'percentage' | 'number' | 'hour' | 'seconds',
  prevContent?: string | number,
  country?: CountryFilterType,
  // TODO: remove this after finalize
  isNewLogic: boolean = false
): string[] {
  const contentValue = Number(content)
  const prevContentValue = Number(prevContent)

  if (
    !isNewLogic &&
    (prevContentValue === 0 ||
      prevContentValue === null ||
      isNaN(prevContentValue))
  )
    return ['unknown', '0']

  const valueChange = contentValue - prevContentValue

  if (valueChange === 0) return ['neutral', '0']
  let valueChangeString = ''
  if (valueChange > 0) {
    valueChangeString = 'up'
  } else {
    valueChangeString = 'down'
  }

  const value = Math.abs(valueChange)

  return [valueChangeString, formatNumber(value, trendType, true, country)]
}

export const TIME_FILTER_OPTIONS = [
  {
    labelKey: 'timeFilters.hourly',
    value: 'hour',
    condition: (diffInDays: number) => diffInDays <= 400,
  },
  {
    labelKey: 'timeFilters.daily',
    value: 'day',
    condition: (diffInDays: number) => diffInDays > 2 && diffInDays <= 30,
  },
  {
    labelKey: 'timeFilters.weekly',
    value: 'week',
    condition: (diffInDays: number) => diffInDays > 14,
  },
  {
    labelKey: 'timeFilters.monthly',
    value: 'month',
    condition: (diffInDays: number) => diffInDays > 60,
  },
  {
    labelKey: 'timeFilters.quarterly',
    value: 'quarter',
    condition: (diffInDays: number) => diffInDays > 180,
  },
  {
    labelKey: 'timeFilters.yearly',
    value: 'year',
    condition: (diffInDays: number) => diffInDays > 365,
  },
  {
    labelKey: 'timeFilters.dayOfWeek',
    value: 'dow',
    condition: (diffInDays: number) => diffInDays > 7,
  },
] as const

export const getMinimumAvailableTime = (
  dateRange: DateRange,
  comparison?: ComparisonSelection | null
): Interval => {
  // Get the minimum available time for the selected date range
  if (comparison?.value === 'period') return 'month'

  if (dateRange.from) {
    const toDate = dateRange.to || dateRange.from // Default to `from` if `to` is undefined
    const dateDiff = Math.ceil(
      (toDate.getTime() - dateRange?.from.getTime()) / (1000 * 60 * 60 * 24)
    )
    const interval = TIME_FILTER_OPTIONS.find((option) =>
      option.condition(dateDiff)
    )?.value
    return interval || 'day'
  }
  return 'day'
}

export const isDateRangeValidForInterval = (
  dateRange: DateRange,
  interval: Interval,
  comparison?: ComparisonSelection | null
): boolean => {
  if (comparison?.type === 'period') {
    // only H, M, Q, K are allowed for period comparison
    return ['hour', 'month', 'quarter', 'dow'].includes(interval)
  }
  const toDate = dateRange.to || dateRange.from
  if (toDate && dateRange.from) {
    const currentDateDiff = Math.ceil(
      (toDate.getTime() - dateRange.from.getTime()) / (1000 * 60 * 60 * 24)
    )
    return (
      TIME_FILTER_OPTIONS.find(
        (option) => option.value === interval
      )?.condition(currentDateDiff) || false
    )
  }
  return false
}

export const getMetricFilters = (
  chartType: ChartType
): { value: string; label: string }[] => {
  if (chartType === 'sales') {
    return SALES_METRIC_FILTERS
  } else if (chartType === 'labor') {
    return LABOR_METRIC_FILTERS
  } else if (chartType === 'speedOfService') {
    return SPEED_OF_SERVICE_METRIC_FILTERS
  } else if (chartType === 'spend') {
    return SPEND_METRIC_FILTERS
  } else if (chartType === 'pnl') {
    return PNL_METRIC_FILTERS
  }
  return []
}

export const getLensMetricFilters = (
  id: string
): { value: string; label: string }[] => {
  if (id === 'pnl-lens') {
    return [
      {
        value: 'netSalesPnL',
        label: 'Net Sales',
      },
      {
        value: 'cogsPnL',
        label: 'COGS',
      },
      {
        value: 'storeLaborPnL',
        label: 'Store Labor',
      },
      {
        value: 'opexPnL',
        label: 'OpEx',
      },
      {
        value: 'ebitdaPnL',
        label: 'EBITDA',
      },
    ]
  }
  return []
}

export const getFixedInterval = (typeFilter: string): Interval | null => {
  if (typeFilter === 'utilization') {
    return 'hour'
  }
  return null
}

export const getMappedRegionBranches = (
  isOtherBranchesAllowed: boolean = false
): {
  regionBranchMap: Map<string, string[]>
  branches: string[]
} => {
  const REGIONS = JSON.parse(
    sessionStorage.getItem('regions') ?? '[]'
  ) as Region[]
  const regionBranchMap = new Map<string, string[]>()
  const allBranches: string[] = []

  REGIONS.forEach((region) => {
    const branchNames = region.branches
      .filter((branch) => isOtherBranchesAllowed || branch.name !== 'Other')
      .map((branch) => branch.name)
    regionBranchMap.set(region.name.toString(), branchNames)
  })

  regionBranchMap.forEach((branchNames) => {
    allBranches.push(...branchNames)
  })

  return {
    regionBranchMap,
    branches: allBranches,
  }
}

export const decodeAndFormatPageId = (
  id: string | undefined,
  type: 'sales-lens' | 'spend-lens'
) => {
  if (type === 'sales-lens' && id) {
    const categoryName = localStorage.getItem('categoryItem') || ''
    const itemName = id
    const materialCode = ''

    return {
      categoryName,
      itemName,
      materialCode,
    }
  } else if (type === 'spend-lens' && id) {
    const categoryName = localStorage.getItem('categoryName') || ''
    const itemName = localStorage.getItem('itemName') || ''
    const materialCode = id

    return {
      categoryName,
      itemName,
      materialCode,
    }
  }
  return null
}

export const getDefaultBranchType = (
  type: TenantKey
): 'all' | 'same' | 'new' | 'regular' => {
  if (type === 'blankstreet') {
    return 'all'
  }
  return 'same'
}

export const getBranchesByTenant = (tenant: TenantKey): string => {
  if (tenant === 'blankstreet') {
    return ALL_STORES_BLANKSTREET_JOINED()
  }
  return SAME_STORES_DEV_JOINED
}

export const formatValueForKpi = (
  value: number,
  unit?: string,
  decimals: number = 1
): string => {
  if (!unit) {
    return decimals > 0 ? value.toFixed(decimals) : value.toString()
  }

  const unitLower = unit.toLowerCase().trim()
  const formattedValue =
    decimals > 0 ? value.toFixed(decimals) : value.toString()

  // Check if unit is dollar-related
  if (
    unitLower === '$' ||
    unitLower === 'dollar' ||
    unitLower === 'dollars' ||
    unitLower === 'US' ||
    unit.startsWith('$')
  ) {
    return `$${formattedValue}`
  }

  // Check if unit is percentage
  if (
    unitLower === '%' ||
    unitLower === 'percent' ||
    unitLower === 'percentage'
  ) {
    return `${formattedValue}%`
  }

  // Check if unit is hour/hours
  if (
    unitLower === 'hour' ||
    unitLower === 'hours' ||
    unitLower === 'hr' ||
    unitLower === 'hrs'
  ) {
    const hourLabel = value === 1 ? 'hour' : 'hours'
    return `${formattedValue} ${hourLabel}`
  }

  // Default: unit at the end
  return `${formattedValue} ${unit}`
}

export const calculateBonusPercentage = (
  assignmentsData: KpiUserAssignmentsModel[]
) => {
  return assignmentsData.reduce((acc, curr) => {
    const isAchieved = curr.achieved === true
    if (isAchieved) {
      return acc + curr.bonusAmount
    }
    return acc
  }, 0) as number
}

export const calculatePayoutAmount = (
  salary: number,
  hrsPerWeek: number,
  weeks: number,
  totalBonusPercentage: number,
  maxEligibleBonusPercentage: number
): {
  eligibleBonusAmount: number
  payoutAmount: number
} => {
  const maxEligibleBonusAmount =
    (salary * hrsPerWeek * weeks * maxEligibleBonusPercentage) / 100

  return {
    eligibleBonusAmount: maxEligibleBonusAmount,
    payoutAmount: (maxEligibleBonusAmount * totalBonusPercentage) / 100,
  }
}

export const calculateRemainingDaysInQuarter = (): number => {
  // TODO: put mock date July 26st 2024 for dev only
  const today = new Date('2024-07-26')
  const currentMonth = today.getMonth()
  const currentYear = today.getFullYear()

  let quarterEnd: Date

  if (currentMonth < 3) {
    // Q1: January - March (ends on March 31)
    quarterEnd = new Date(currentYear, 2, 31)
  } else if (currentMonth < 6) {
    // Q2: April - June (ends on June 30)
    quarterEnd = new Date(currentYear, 5, 30)
  } else if (currentMonth < 9) {
    // Q3: July - September (ends on September 30)
    quarterEnd = new Date(currentYear, 8, 30)
  } else {
    // Q4: October - December (ends on December 31)
    quarterEnd = new Date(currentYear, 11, 31)
  }

  const timeDiff = quarterEnd.getTime() - today.getTime()
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24))

  return daysDiff
}

export const calculateTrailingGoalData = (
  currentData: number,
  kpiGoalData: number,
  pageId:
    | 'speed-of-service'
    | 'sales'
    | 'labor'
    | 'spend'
    | 'loyalty'
    | 'profit-and-loss'
): number => {
  if (pageId === 'speed-of-service' || pageId === 'labor') {
    if (kpiGoalData >= currentData) {
      return 0
    }

    const trailingGoal = 2 * kpiGoalData - currentData
    return Math.max(trailingGoal, Math.round(kpiGoalData * 0.75))
  }

  if (currentData >= kpiGoalData) {
    return 0
  }

  return kpiGoalData * 2 - currentData
}
